import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button,
} from "@mui/material";

/**
 * OPUS-893: app-wide styled confirmation (replaces native window.confirm so
 * confirmations look like the rest of the app). Usage:
 *   const confirm = useConfirm();
 *   if (await confirm({ title, message, destructive: true })) { ... }
 */
const ConfirmContext = createContext(null);

export const ConfirmProvider = ({ children }) => {
  const [state, setState] = useState({
    open: false, title: "Are you sure?", message: "",
    confirmText: "Confirm", cancelText: "Cancel", destructive: false,
  });
  const resolver = useRef(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolver.current = resolve;
      setState({
        open: true,
        title: opts.title || "Are you sure?",
        message: opts.message || "",
        confirmText: opts.confirmText || "Confirm",
        cancelText: opts.cancelText || "Cancel",
        destructive: !!opts.destructive,
      });
    });
  }, []);

  const close = (result) => {
    setState((s) => ({ ...s, open: false }));
    if (resolver.current) { resolver.current(result); resolver.current = null; }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={state.open} onClose={() => close(false)} fullWidth maxWidth="xs">
        <DialogTitle>{state.title}</DialogTitle>
        {state.message ? (
          <DialogContent>
            <DialogContentText sx={{ color: "text.secondary" }}>{state.message}</DialogContentText>
          </DialogContent>
        ) : null}
        <DialogActions>
          <Button onClick={() => close(false)} color="info">{state.cancelText}</Button>
          <Button
            onClick={() => close(true)}
            variant="contained"
            color={state.destructive ? "error" : "seafoamGreen"}
            autoFocus
          >
            {state.confirmText}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within a ConfirmProvider");
  return ctx;
};

export default ConfirmProvider;
